import { useTranslate } from '@refinedev/core';
import { DateField } from '@refinedev/antd';
import { Card, Table, Tooltip } from 'antd';
import dayjs from 'dayjs';
import type { ChargeDailyOut, ChargeMonthlyOut } from '@/api/generated/schemas';
import { useAdminTable } from '@/hooks/useAdminTable';
import { getSortOrder } from '@/utils/sorters';
import { formatDuration } from '@/utils/duration';
import { formatLedger, formatPayable } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { UnitPriceCell } from '@/components/settlement/UnitPriceCell';
import { NUMERIC_STYLE } from '@/components/settlement/numeric';

const T = 'settlement.charges_monthly';

/** A month has at most 31 daily rows, so one request holds the whole month. */
const MONTH_LIMIT = 31;

/**
 * The daily rows behind one monthly aggregate: the same six grouping
 * dimensions, narrowed to the month. Their amounts add up to the monthly
 * amount.
 */
export const ChargeDailyTab = ({ record }: { record: ChargeMonthlyOut }) => {
  const t = useTranslate();
  const month = dayjs(record.bucket).startOf('month');

  const { tableProps, sorters } = useAdminTable<ChargeDailyOut>({
    resource: 'settlement.charges_daily',
    pagination: { pageSize: MONTH_LIMIT },
    sorters: { initial: [{ field: 'bucket', order: 'asc' }] },
    filters: {
      permanent: [
        {
          field: 'bucket',
          operator: 'gte',
          value: month.format('YYYY-MM-DD'),
        },
        {
          field: 'bucket',
          operator: 'lt',
          value: month.add(1, 'month').format('YYYY-MM-DD'),
        },
        { field: 'publisher_id', operator: 'eq', value: record.publisher_id },
        { field: 'account_id', operator: 'eq', value: record.account_id },
        { field: 'profile_id', operator: 'eq', value: record.profile_id },
        { field: 'product_id', operator: 'eq', value: record.product_id },
        {
          field: 'product_version',
          operator: 'eq',
          value: record.product_version,
        },
        {
          field: 'contract_rate_id',
          operator: 'eq',
          value: record.contract_rate_id,
        },
      ],
    },
  });

  return (
    <Card title={t(`${T}.sections.daily`)}>
      {/* One request covers the month, so the pager has nothing to page. */}
      <Table {...tableProps} pagination={false} rowKey="id">
        <Table.Column
          dataIndex="bucket"
          title={t(`${T}.fields.bucket_day`)}
          sorter
          sortOrder={getSortOrder(sorters, 'bucket')}
          render={(value: string) => (
            <DateField value={value} format="YYYY-MM-DD" />
          )}
        />
        <Table.Column
          dataIndex="basis_seconds"
          title={t(`${T}.fields.basis_seconds`)}
          sorter
          sortOrder={getSortOrder(sorters, 'basis_seconds')}
          render={(value: number) => (
            <Tooltip title={`${value}s`}>{formatDuration(value)}</Tooltip>
          )}
        />
        <Table.Column
          dataIndex="charges_count"
          title={t(`${T}.fields.charges_count`)}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'charges_count')}
        />
        {/* Column titles drop the "unit price" words — the group header
            carries them. */}
        <Table.ColumnGroup title={t(`${T}.fields.unit_price`)}>
          <Table.Column
            dataIndex="unit_price"
            title={
              <HintLabel
                label={t(`${T}.columns.ledger`)}
                hint={t('settlement.hints.unit_price')}
              />
            }
            align="right"
            render={(value: string, row: ChargeDailyOut) => (
              <UnitPriceCell unitPrice={value} currency={row.currency} />
            )}
          />
          <Table.Column
            dataIndex="payable_unit_price"
            title={
              <HintLabel
                label={t(`${T}.columns.invoice`)}
                hint={t('settlement.hints.payable_unit_price')}
              />
            }
            align="right"
            render={(value: string, row: ChargeDailyOut) => (
              <span style={NUMERIC_STYLE}>
                {formatPayable(value, row.currency)}
              </span>
            )}
          />
        </Table.ColumnGroup>
        {/* No invoice amount here by design — see the monthly list. */}
        <Table.Column
          dataIndex="amount"
          title={t(`${T}.fields.amount`)}
          align="right"
          sorter
          sortOrder={getSortOrder(sorters, 'amount')}
          render={(value: string, row: ChargeDailyOut) => (
            <span style={NUMERIC_STYLE}>
              {formatLedger(value, row.currency)}
            </span>
          )}
        />
      </Table>
    </Card>
  );
};
